import { FC, useCallback, useState, useEffect } from "react";
import { Box, Typography, Stack, Alert } from "@mui/material";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";
import { grey } from "@mui/material/colors";
import { toast } from "react-toastify";
import theme from "src/configs/theme";
import uploadSvg from "src/assets/icons/upload_file.svg";
import { UploadFileCard } from "./UploadFileCard";
import { getVideoDuration } from "./utils";
import VideoEditorDialog from "./Editor/VideoEditorDialog";

const supportedDuration = 5; // in minutes
const supportedSize = 400; // in MB

type DropzonePropsType = {
  dropzoneOptions?: DropzoneOptions;
  onChange: (files: File[]) => void;
  dropzoneText?: string;
  multiple?: boolean;
  fileUploadStates?: FileUploadState[];
  onCancel?: (index: number) => void;
  allowEditing?: boolean;
  limitCount?: number;
};

type FileUploadState = {
  file: File;
  progress: number;
  status: "uploading" | "success" | "error";
  abortController: AbortController;
};

const Dropzone: FC<DropzonePropsType> = ({
  dropzoneOptions = {
    maxSize: supportedSize * 1024 * 1024,
    accept: {
      "video/*": [
        ".mp4",
        ".mkv",
        ".webm",
        ".ogg",
        ".mov",
        ".wmv",
        ".m4v",
      ],
    },
  },
  onChange,
  dropzoneText,
  multiple = false,
  fileUploadStates = [],
  onCancel,
  allowEditing = true,
  limitCount = Infinity,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileToEdit, setFileToEdit] = useState<File | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState<boolean>(false);

  useEffect(() => {
    if (selectedFiles.length > limitCount) {
      const limitedFiles = selectedFiles.slice(0, limitCount);
      setSelectedFiles(limitedFiles);
      onChange(limitedFiles);
      toast.warning(
        `Only ${limitCount} file(s) can be uploaded due to your subscription limit.`,
      );
    }
  }, [limitCount, onChange, selectedFiles]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      handleFileRejections(fileRejections);

      const remainingSlots = limitCount - selectedFiles.length;

      if (remainingSlots <= 0) {
        toast.warning(
          `You have reached your upload limit of ${limitCount} file(s).`,
        );
        return;
      }

      const limitedAcceptedFiles = acceptedFiles.slice(0, remainingSlots);

      if (limitedAcceptedFiles.length < acceptedFiles.length) {
        toast.warning(
          `Only ${remainingSlots} more file(s) can be added due to your subscription limit.`,
        );
      }

      const validFilesPromises = limitedAcceptedFiles.map((file) =>
        getVideoDuration(file).then((duration) => {
          if (Math.round(duration) >= 60 * supportedDuration + 10) {
            toast.error(
              `Selected file length is more than ${supportedDuration} minutes`,
            );
            return null;
          } else {
            return file;
          }
        }),
      );

      Promise.all(validFilesPromises).then((validFiles) => {
        const nonNullFiles = validFiles.filter(
          (file) => file !== null,
        ) as File[];

        if (nonNullFiles.length > 0) {
          setSelectedFiles((prevFiles) =>
            multiple ? [...prevFiles, ...nonNullFiles] : nonNullFiles,
          );
          onChange(
            multiple ? [...selectedFiles, ...nonNullFiles] : nonNullFiles,
          );
        }
      });
    },
    [multiple, onChange, selectedFiles, limitCount],
  );

  const handleFileRejections = (fileRejections: FileRejection[]) => {
    fileRejections.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === "file-too-large") {
          toast.error(`Error: File is larger than ${supportedSize} MB`);
        }
        if (err.code === "file-invalid-type") {
          toast.error(`Error: ${err.message}`);
        }
      });
    });
  };

  const removeFileHandler = (indexToRemove: number) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove),
    );
    onChange(selectedFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleEditClick = (file: File) => {
    setFileToEdit(file);
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setFileToEdit(null);
  };

  const handleEditComplete = (processedFile: File) => {
    // Replace the original file with the processed one
    const updatedFiles = selectedFiles.map((file) =>
      file.name === fileToEdit?.name ? processedFile : file,
    );

    setSelectedFiles(updatedFiles);
    onChange(updatedFiles);
    setIsEditorOpen(false);
    setFileToEdit(null);

    toast.success(`Video edited successfully!`);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    ...dropzoneOptions,
    multiple,
  });

  const remainingSlots = limitCount - selectedFiles.length;

  const limitInfo = limitCount < Infinity && (
    <Typography
      fontWeight="bold"
      align="center"
      sx={{
        color: remainingSlots > 0 ? grey[400] : "error.main",
        mt: 1,
      }}
    >
      {remainingSlots > 0
        ? `* You can upload ${remainingSlots} more file${remainingSlots !== 1 ? "s" : ""} (limit: ${limitCount})`
        : `* Upload limit reached (${limitCount} files maximum)`}
    </Typography>
  );

  const dropzoneContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <img src={uploadSvg} alt="upload_dropzone" />
      <Typography
        sx={{ color: grey[400] }}
        fontWeight="bold"
        align="center"
        lineHeight="2rem"
      >
        {dropzoneText ||
          `Drag and Drop ${
            multiple ? "video files" : "a video file"
          } here or click to select`}
      </Typography>
      <Typography fontWeight="bold" align="center" sx={{ color: grey[400] }}>
        {`* Supported file duration is less than ${supportedDuration} minute${
          supportedDuration > 1 ? "s" : ""
        }`}
      </Typography>
      <Typography fontWeight="bold" align="center" sx={{ color: grey[400] }}>
        {`* Supported file size is less than ${supportedSize} MB`}
      </Typography>
      <Typography fontWeight="bold" sx={{ color: grey[400] }} align="center">
        * Supported file types: .mp4, .mkv, .webm, .ogg, .mov, .wmv, .m4v
      </Typography>
      {allowEditing && (
        <Typography fontWeight="bold" sx={{ color: grey[400] }} align="center">
          * Videos can be trimmed and cropped before uploading
        </Typography>
      )}
      {limitInfo}
    </Box>
  );

  const limitAlert =
    limitCount < Infinity &&
    (remainingSlots === 0 ? (
      <Alert severity="warning" sx={{ mb: 2 }}>
        You've reached the maximum of {limitCount} files. Remove some files to
        add new ones.
      </Alert>
    ) : remainingSlots <= 2 ? (
      <Alert severity="info" sx={{ mb: 2 }}>
        You can add {remainingSlots} more file{remainingSlots !== 1 ? "s" : ""}{" "}
        before reaching your limit.
      </Alert>
    ) : null);

  return (
    <>
      {limitAlert}
      <Box
        sx={{
          width: "100%",
          border: "1px dashed rgba(0, 0, 0, 0.5)",
          borderRadius: theme.spacing(1),
          padding: theme.spacing(4, 2),
          cursor: "pointer",
        }}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        {(selectedFiles.length === 0 || multiple) && dropzoneContent}
        <Box mt={2}>
          {selectedFiles.map((file, index) => (
            <Stack
              direction="row"
              key={index}
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <Box flexGrow={1}>
                <UploadFileCard
                  file={file}
                  progress={fileUploadStates[index]?.progress || 0}
                  status={fileUploadStates[index]?.status || "uploading"}
                  handleEditClick={handleEditClick}
                  cancel={() => {
                    removeFileHandler(index);
                    onCancel && onCancel(index);
                  }}
                />
              </Box>
            </Stack>
          ))}
        </Box>
      </Box>

      {fileToEdit && (
        <VideoEditorDialog
          open={isEditorOpen}
          onClose={handleEditorClose}
          videoFile={fileToEdit}
          onEditComplete={handleEditComplete}
        />
      )}
    </>
  );
};

export default Dropzone;
